/**
 * The shell every full-screen game panel is built on.
 *
 * ─── Why this exists ────────────────────────────────────────────────────────
 * Six panels — vendor, quest journal, banker, currency shop, black market and
 * world map — each built the same five elements by hand, in the same order,
 * with the same inline style strings:
 *
 *   a fixed scrim, a `.panel grained` frame, a close cross in the top-right,
 *   a header block, a body block.
 *
 * Six copies of a five-element shell is five chances to drift per panel, and
 * the drift is invisible: nothing throws when one window's close cross ends up
 * two pixels off or one scrim is a shade darker. `ui/panels.ts` is the same
 * argument applied to open/close STATE; this is the argument applied to the
 * markup. The CSS half lives in `styles/window.css.ts`.
 *
 * ─── What is deliberately still the caller's ────────────────────────────────
 *  - The element ids (`shop-ui`, `shop-header`, `shop-body`, …). They are the
 *    panels' own open/close handles and their tests' selectors, so they are
 *    passed in rather than invented here.
 *  - `style.display`. Every panel toggles it and reads it back in its own
 *    `isOpen()`; moving that into a class would be a behaviour change dressed
 *    as a style cleanup. The shell sets the initial `none` and nothing else.
 *  - Escape, the backdrop click, the store subscriptions. Those differ per
 *    panel (the banker has a second confirm layer, the shop steps out of the
 *    deed prompt first) and collapsing them here would mean a callback per
 *    difference, which is the abstraction being worse than the duplication.
 */

import { goldIconHtml } from './hud';
import { cashCoinHtml } from './coin';
import { createCloseButton } from './close-button';

export interface WindowChrome {
  /** The full-screen scrim. Append this to the container; toggle its display. */
  root: HTMLElement;
  /** The framed window inside the scrim. */
  panel: HTMLElement;
  /** Title/subtitle/balance area — the caller writes into it. */
  header: HTMLElement;
  /** Everything below the header. */
  body: HTMLElement;
  close: HTMLButtonElement;
}

export function createWindowChrome(opts: {
  /** Element id for the scrim — the panel's own open/close handle. */
  id: string;
  /** Element id for the header block, when the panel's code or tests address it. */
  headerId?: string;
  /** Element id for the body block. */
  bodyId?: string;
  /** Max window width in px. The one thing the six panels genuinely disagreed about. */
  width: number;
  /** For the close button's `aria-label` — "Close shop", "Close the map". */
  label: string;
  /** Called by the close cross AND by a click on the scrim itself. */
  onClose: () => void;
  /** Extra classes for the body, for panels that lay theirs out as a grid or column. */
  bodyClass?: string;
}): WindowChrome {
  const root = document.createElement('div');
  root.id = opts.id;
  root.className = 'bv-scrim';
  // Inline, not a class: see the header — `isOpen()` reads this back.
  root.style.display = 'none';

  const panel = document.createElement('div');
  panel.className = 'panel grained bv-win';
  // The only per-panel dimension left at a call site.
  panel.style.setProperty('--win-w', `${opts.width}px`);

  const close = createCloseButton({ label: opts.label, className: 'bv-win-close', onClick: () => opts.onClose() });

  const header = document.createElement('div');
  if (opts.headerId) header.id = opts.headerId;

  const body = document.createElement('div');
  if (opts.bodyId) body.id = opts.bodyId;
  body.className = opts.bodyClass ? `bv-win-body ${opts.bodyClass}` : 'bv-win-body';

  panel.append(close, header, body);
  root.appendChild(panel);

  // Backdrop-to-close, on the SCRIM only. `e.target === root` matters: without
  // it a click anywhere inside the window bubbles up here and closes it, which
  // is the bug that makes a panel feel like it cannot be used.
  root.addEventListener('pointerdown', (e) => {
    if (e.target === root) opts.onClose();
  });

  return {
    root, panel, header, body, close,
  };
}

/**
 * An amount of Gold: the coin icon, the number, and the word for anyone who
 * cannot see the coin.
 *
 * ─── Why one function and not four inline strings ───────────────────────────
 * Gold was rendered four different ways across the shop, the Gold shop (twice)
 * and the banker — a star glyph in some places, the bare word "Gold" in
 * others, while the HUD had been showing the real coin art since it was
 * generated. A currency that looks like three different things is three
 * different currencies to a player.
 *
 * The icon is `aria-hidden` and decorative by design (`goldIconHtml`'s own
 * comment: "gold coin 3" reads worse than "3 Gold"), which is exactly why the
 * word cannot simply be deleted — it moves into a screen-reader-only span
 * instead, so the sentence a screen reader reads is still "3 Gold".
 */
export function goldAmount(value: number | string): string {
  return `<span class="bv-win-gold">${goldIconHtml('bv-coin')}`
    + `${amountHtml(value)}<span class="bv-sr"> Gold</span></span>`;
}

/** The same, for Cash: the spinning coin, the number, and the unit for screen readers only. */
export function cashAmount(value: number | string): string {
  return `<span class="bv-win-cash">${cashCoinHtml('md')}`
    + `${amountHtml(value)}<span class="bv-sr"> Cash</span></span>`;
}

/**
 * Thousands as K, millions as KK: 2 500 -> 2.5K, 95 600 -> 95.6K, 1 000 000 ->
 * 1KK. Janeks, 2026-10-19: *"On mobile, use "K" for thousands "KK" for Mil"*
 * (eight Gold packs in a phone row ran their numbers together). Rounds DOWN,
 * so a short figure never claims more than the long one. Null under 1 000 or
 * for anything that is not a plain number.
 */
export function compactAmount(value: number | string): string | null {
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[,\s]/g, ''));
  if (!Number.isFinite(n) || Math.abs(n) < 1000) return null;
  const [div, unit] = Math.abs(n) >= 1_000_000 ? [1_000_000, 'KK'] : [1_000, 'K'];
  const short = Math.floor((n / div) * 10) / 10;
  return `${Number.isInteger(short) ? short : short.toFixed(1)}${unit}`;
}

/**
 * The number, twice when it has a short form: the stylesheet shows the long
 * one from tablet width up and the short one below it (`.bv-amt-*` in
 * window.css.ts). Only one is ever displayed, so a screen reader reads one.
 */
function amountHtml(value: number | string): string {
  const long = escapeHtml(String(value));
  const short = compactAmount(value);
  return short === null
    ? long
    : `<span class="bv-amt-long">${long}</span><span class="bv-amt-short">${escapeHtml(short)}</span>`;
}

/** A title + optional subtitle, the way every window writes its own. */
export function windowHeading(title: string, sub?: string): string {
  return `<h2 class="bv-win-title">${escapeHtml(title)}</h2>`
    + (sub ? `<p class="bv-win-sub">${escapeHtml(sub)}</p>` : '');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}
